import React, { useState } from 'react';
import { TagIcon } from '@heroicons/react/outline';
import { criterionOptionsFor, tagChipLabel, tagSourceLabel } from '../../constants/evidenceTagging';
import EvidenceTagForm from './EvidenceTagForm';

/**
 * EvidenceTagControl
 * ------------------
 * The Tag button on each category heading in the history review (TDI-394,
 * Tom King's ruling R1, 27 October 2026). One button per category, whoever
 * answered it: where the school and the parent or carer both answered, the
 * menu's "Which part?" lists each line with whose answer it is, and the tag
 * takes its source from the line chosen.
 *
 * A clinician reads the history review before the decision screens, so this
 * is where they mark what matters: a criterion, a risk, an other factor, or
 * something to add to their conclusion, with an optional note. The decision
 * screens then show exactly what they tagged and nothing else.
 *
 * Tags are the clinician's own: nothing here suggests, pre-selects or infers.
 *
 * Props:
 *   - pathway: 'adhd' | 'asd'
 *   - label: the category heading, for the menu and the button's name
 *   - targets: [{ formKey, role, questionId, label, values: string[] }]
 *   - tags: the caller's existing tags on these targets
 *   - onAdd(tag), onRemove(tagId): async; errors are surfaced by the caller
 *   - disabled, disabledReason: tagging is closed; the reason is the button's
 *     tooltip, so a clinician is told what to do rather than left guessing
 */

/** Truncate a line for a chip. */
const shortLine = (text, max = 24) =>
  (text || '').length > max ? `${(text || '').slice(0, max - 1)}…` : (text || '');

export default function EvidenceTagControl({
  pathway,
  label,
  targets = [],
  tags = [],
  onAdd,
  onRemove,
  disabled = false,
  disabledReason = 'Tagging is not available on this referral',
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const answered = targets.filter((t) => t && (t.values || []).length > 0);
  // Nothing to point at, or no pathway to tag against: the control would be a
  // button that can never do anything.
  if (!answered.length || !criterionOptionsFor(pathway).length) return null;
  const heading = label || answered[0].label;

  const handleRemove = async (tagId) => {
    if (busy) return;
    setBusy(true);
    try {
      await onRemove(tagId);
    } finally {
      setBusy(false);
    }
  };

  const buttonLabel = tags.length
    ? `Tag — ${heading}, ${tags.length} ${tags.length === 1 ? 'tag' : 'tags'}`
    : `Tag — ${heading}`;

  return (
    <div className="relative mt-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-expanded={open}
        aria-label={buttonLabel}
        title={disabled ? disabledReason : undefined}
        className={`inline-flex min-h-[2rem] items-center gap-1 rounded px-1.5 text-xs font-medium ${
          disabled
            ? 'cursor-not-allowed text-gray-300'
            : 'cursor-pointer text-primary hover:bg-primary/10'
        }`}
      >
        <TagIcon className="h-4 w-4" aria-hidden="true" />
        <span>Tag</span>
        {tags.length > 0 && (
          <span
            className="inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-white"
            aria-hidden="true"
          >
            {tags.length}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute left-0 z-40 mt-1 w-80 rounded-lg border border-gray-200 bg-white p-3 text-left shadow-xl">
          <p className="text-xs font-semibold text-gray-900">{heading}</p>

          {tags.length > 0 && (
            <ul className="mt-2 flex flex-wrap gap-1.5" aria-label={`Your tags on ${heading}`}>
              {tags.map((tag) => (
                <li
                  key={tag.id}
                  className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary ring-1 ring-inset ring-primary/20"
                >
                  {tagChipLabel(pathway, tag)}
                  {tagSourceLabel(tag.source) && (
                    <span className="font-normal text-primary/80">· {tagSourceLabel(tag.source)}</span>
                  )}
                  {typeof tag.partIndex === 'number' && (
                    <span className="font-normal text-primary/80">· “{shortLine(tag.value)}”</span>
                  )}
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => handleRemove(tag.id)}
                      disabled={busy}
                      className="text-primary/70 hover:text-primary disabled:opacity-50"
                      aria-label={`Remove ${tagChipLabel(pathway, tag)} tag — ${heading}`}
                    >
                      ×
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          <EvidenceTagForm
            pathway={pathway}
            targets={answered}
            onSubmit={onAdd}
            onClose={() => setOpen(false)}
          />
        </div>
      )}
    </div>
  );
}
